import { useState, useEffect } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const OVH_PRESET = {
    host: "ssl0.ovh.net",
    port: 465,
    use_ssl: true,
    use_starttls: false,
};

export default function SmtpSettingsCard() {
    const { data: cfg, mutate } = useSWR("/settings/smtp", fetcher);
    const [form, setForm] = useState({
        host: "",
        port: 465,
        use_ssl: true,
        use_starttls: false,
        user: "",
        password: "",
        from_addr: "",
        from_name: "ScanBox",
    });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testTo, setTestTo] = useState("");

    useEffect(() => {
        if (!cfg || !cfg.configured) return;
        setForm((f) => ({
            ...f,
            host: cfg.host || "",
            port: cfg.port || 465,
            use_ssl: cfg.use_ssl ?? true,
            use_starttls: cfg.use_starttls ?? false,
            user: cfg.user || "",
            from_addr: cfg.from_addr || "",
            from_name: cfg.from_name || "ScanBox",
            // never read back the password — leave blank, user re-enters only on change
            password: "",
        }));
    }, [cfg]);

    const applyPreset = () => {
        setForm((f) => ({ ...f, ...OVH_PRESET }));
        toast.message("Préréglage OVH appliqué");
    };

    const save = async () => {
        if (!form.host || !form.from_addr) {
            toast.error("Renseignez au moins l'hôte SMTP et l'adresse expéditeur.");
            return;
        }
        setSaving(true);
        try {
            // Don't send empty password — backend preserves the existing one
            const payload = { ...form };
            if (!payload.password) delete payload.password;
            await api.put("/settings/smtp", payload);
            toast.success("Configuration SMTP enregistrée");
            await mutate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/settings/smtp/test", testTo ? { to: testTo } : {});
            toast.success(`Email de test envoyé à ${data.sent_to} — vérifiez votre boîte`, { duration: 8000 });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec du test — vérifiez vos paramètres");
        } finally {
            setTesting(false);
        }
    };

    return (
        <Card className="ec-card">
            <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <EnvelopeSimple size={20} weight="duotone" className="text-primary" />
                        <h3 className="font-display text-lg font-bold tracking-tight">
                            Configuration SMTP
                        </h3>
                        {cfg?.configured && (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5">
                                <CheckCircle size={12} weight="fill" /> configuré
                            </span>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={applyPreset}
                        data-testid="smtp-preset-ovh"
                    >
                        Préréglage OVH
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                    Utilisé pour les emails système (réinitialisation de mot de passe,
                    bienvenue). Vos identifiants SMTP sont chiffrés en base. Avec OVH,
                    utilisez <code className="bg-muted px-1 rounded">ssl0.ovh.net</code>
                    {" "}sur le port <code className="bg-muted px-1 rounded">465</code> en SSL.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs">Hôte SMTP</Label>
                        <Input
                            value={form.host}
                            onChange={(e) => setForm({ ...form, host: e.target.value })}
                            placeholder="ssl0.ovh.net"
                            data-testid="smtp-host"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Port</Label>
                        <Input
                            type="number"
                            value={form.port}
                            onChange={(e) => setForm({ ...form, port: parseInt(e.target.value, 10) || 465 })}
                            placeholder="465"
                            data-testid="smtp-port"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={form.use_ssl}
                            onCheckedChange={(v) => setForm({ ...form, use_ssl: v, use_starttls: v ? false : form.use_starttls })}
                            data-testid="smtp-ssl"
                        />
                        <Label className="text-sm">SSL / TLS (port 465)</Label>
                    </div>
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={form.use_starttls}
                            onCheckedChange={(v) => setForm({ ...form, use_starttls: v, use_ssl: v ? false : form.use_ssl })}
                            data-testid="smtp-starttls"
                        />
                        <Label className="text-sm">STARTTLS (port 587)</Label>
                    </div>
                    <div>
                        <Label className="text-xs">Utilisateur SMTP</Label>
                        <Input
                            value={form.user}
                            onChange={(e) => setForm({ ...form, user: e.target.value })}
                            placeholder="contact@votredomaine.fr"
                            data-testid="smtp-user"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">
                            Mot de passe SMTP {cfg?.configured && <span className="text-muted-foreground">(laissez vide pour conserver)</span>}
                        </Label>
                        <Input
                            type="password"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            placeholder={cfg?.configured ? "••••••••" : ""}
                            data-testid="smtp-password"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Adresse expéditeur</Label>
                        <Input
                            value={form.from_addr}
                            onChange={(e) => setForm({ ...form, from_addr: e.target.value })}
                            placeholder="noreply@scanbox.fr"
                            data-testid="smtp-from-addr"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Nom expéditeur</Label>
                        <Input
                            value={form.from_name}
                            onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                            placeholder="ScanBox"
                            data-testid="smtp-from-name"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2">
                    <Button onClick={save} disabled={saving} data-testid="smtp-save">
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </Button>
                    <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                        <Input
                            type="email"
                            value={testTo}
                            onChange={(e) => setTestTo(e.target.value)}
                            placeholder="Envoyer le test à (vide = votre compte)"
                            className="max-w-xs"
                            data-testid="smtp-test-to"
                        />
                        <Button
                            variant="outline"
                            onClick={sendTest}
                            disabled={testing || !cfg?.configured}
                            data-testid="smtp-test"
                        >
                            <PaperPlaneTilt size={16} className="mr-2" />
                            {testing ? "Envoi…" : "Envoyer un test"}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
